import React from 'react';
import { flushSync } from 'react-dom';
import { ArrowDownUp, Check, Plus, Search } from 'lucide-react';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import type { TaskGroup, TaskGroupBy } from '../taskViews';
import type { Task, TaskMember } from '../types';
import { TaskRow } from './TaskRow';
import { MobileCreateButton, SegmentedControl, TONE_TEXT_CLASS, TASK_SECTION_LABEL_CLASS, isTypingTarget } from './taskUi';

const GROUP_OPTIONS: { value: TaskGroupBy; label: string }[] = [
  { value: 'faelligkeit', label: 'Fälligkeit' },
  { value: 'prioritaet', label: 'Priorität' },
  { value: 'person', label: 'Person' },
];

const MOBILE_ICON_BTN_CLASS =
  'flex h-10 w-10 items-center justify-center rounded-md text-app-text-subtle transition-colors active:bg-app-hover';

/** Mobil: Gruppieren als kleines Menü hinter einem Icon. */
function GroupByMenu({ value, onChange }: { value: TaskGroupBy; onChange: (g: TaskGroupBy) => void }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label="Gruppieren nach"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`${MOBILE_ICON_BTN_CLASS} ${open ? 'bg-app-bg text-app-text' : ''}`}
      >
        <ArrowDownUp className="h-[18px] w-[18px]" strokeWidth={1.8} />
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Gruppieren nach"
          className="absolute right-0 top-full z-30 mt-1.5 w-52 rounded-lg border border-app-border bg-app-surface py-1 shadow-lg"
        >
          <div className={`px-3.5 pb-1 pt-1.5 ${TASK_SECTION_LABEL_CLASS}`}>Gruppieren nach</div>
          {GROUP_OPTIONS.map((o) => {
            const active = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                className={`flex h-11 w-full items-center justify-between px-3.5 text-left text-sm active:bg-app-hover ${
                  active ? 'font-medium text-pipedrive-blue' : 'text-app-text'
                }`}
              >
                {o.label}
                {active && <Check className="h-4 w-4" strokeWidth={2.2} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

type Props = {
  title: string;
  subtitle: string;
  groups: TaskGroup[];
  loading: boolean;
  /** Laden fehlgeschlagen — dann kein „Alles erledigt" anzeigen */
  failed?: boolean;
  membersById: Map<string, TaskMember>;
  today: string;
  selectedId: string | null;
  /** Neue To-dos anlegen (Schnell-Eingabe) */
  canEdit: boolean;
  /** Dieses To-do abhaken/bearbeiten (eigene vs. alle) */
  canEditTask: (task: Task) => boolean;
  /** Schnell-Eingabe zeigen (nicht in „Überfällig" — neue To-dos wären dort sofort unsichtbar) */
  showQuickAdd: boolean;
  /** Gruppieren-Auswahl zeigen (nicht in „Erledigt", dort fest nach Erledigt-Datum) */
  showGroupBy: boolean;
  emptyTitle: string;
  emptyText: string;
  groupBy: TaskGroupBy;
  onGroupBy: (g: TaskGroupBy) => void;
  query: string;
  onQuery: (q: string) => void;
  /** Ansichts-Auswahl bei schmalen Fenstern */
  viewSelect?: React.ReactNode;
  /** Mobil: Ansichts-Chips unter der Überschrift */
  viewChips?: React.ReactNode;
  /** Mobil: „+"-Knopf in der Kopfzeile */
  onCreate?: () => void;
  quickInputRef: React.RefObject<HTMLInputElement | null>;
  onQuickAdd: (title: string) => void;
  onSelect: (task: Task) => void;
  onToggle: (task: Task) => void;
  /** ↑/↓ bzw. j/k, Enter, Leertaste/X — aus, solange z. B. das Anlege-Panel offen ist */
  keyboardEnabled: boolean;
  /** Unter der Liste, z. B. „Ältere erledigte laden" */
  footer?: React.ReactNode;
};

export function TaskList({
  title,
  subtitle,
  groups,
  loading,
  failed = false,
  membersById,
  today,
  selectedId,
  canEdit,
  canEditTask,
  showQuickAdd,
  showGroupBy,
  emptyTitle,
  emptyText,
  groupBy,
  onGroupBy,
  query,
  onQuery,
  viewSelect,
  viewChips,
  onCreate,
  quickInputRef,
  onQuickAdd,
  onSelect,
  onToggle,
  keyboardEnabled,
  footer,
}: Props) {
  const [quick, setQuick] = React.useState('');
  const [focusedId, setFocusedId] = React.useState<string | null>(null);
  /** Mobil steckt die Suche hinter der Lupe; mit Suchtext bleibt sie offen. */
  const [searchOpen, setSearchOpen] = React.useState(false);
  const searchRef = React.useRef<HTMLInputElement>(null);
  const showSearch = searchOpen || Boolean(query);

  const flat = React.useMemo(() => groups.flatMap((g) => g.tasks), [groups]);
  const lastIndexRef = React.useRef(0);
  const focusedIndex = focusedId ? flat.findIndex((t) => t.id === focusedId) : -1;
  if (focusedIndex >= 0) lastIndexRef.current = focusedIndex;
  React.useEffect(() => {
    // Fokussierte Zeile verschwunden (z. B. erledigt) → auf die Zeile an gleicher Stelle springen.
    if (focusedId && focusedIndex < 0) setFocusedId(flat[Math.min(lastIndexRef.current, flat.length - 1)]?.id ?? null);
  }, [flat, focusedId, focusedIndex]);

  const latest = React.useRef({ flat, focusedIndex, canEditTask, onSelect, onToggle });
  latest.current = { flat, focusedIndex, canEditTask, onSelect, onToggle };

  React.useEffect(() => {
    if (!keyboardEnabled) return;
    const move = (delta: number) => {
      const { flat: list, focusedIndex: idx } = latest.current;
      if (!list.length) return;
      const next = idx < 0 ? (delta > 0 ? 0 : list.length - 1) : Math.min(Math.max(idx + delta, 0), list.length - 1);
      const id = list[next].id;
      setFocusedId(id);
      // Echten Fokus mitziehen — sonst reagiert bei Enter noch die zuvor angeklickte Zeile.
      const row = document.querySelector<HTMLElement>(`[data-task-id="${CSS.escape(id)}"]`);
      row?.focus({ preventScroll: true });
      row?.scrollIntoView({ block: 'nearest' });
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // Aus Suche/Schnell-Eingabe heraus: ↑/↓ springt in die Liste, Esc verlässt das Feld.
      const listInput =
        e.target instanceof HTMLElement && e.target.hasAttribute('data-todo-list-input') ? e.target : null;
      if (listInput) {
        if (e.key === 'Escape') {
          listInput.blur();
          return;
        }
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        if (e.isComposing) return;
        listInput.blur();
      } else if (isTypingTarget(e.target)) {
        return;
      }
      if (document.querySelector('[data-app-select-list], [role="dialog"][aria-modal="true"]')) return;
      const { flat: list, focusedIndex: idx } = latest.current;
      // Enter/Leertaste/X nur, wenn nichts anderes (Button, Link …) den Fokus hat.
      const target = e.target instanceof HTMLElement ? e.target : null;
      const free = !target || target === document.body || target.hasAttribute('data-task-id');
      const current = idx >= 0 && free ? list[idx] : null;
      switch (e.key) {
        case 'ArrowDown':
        case 'j':
          e.preventDefault();
          move(1);
          break;
        case 'ArrowUp':
        case 'k':
          e.preventDefault();
          move(-1);
          break;
        case 'Enter':
          if (!current) return;
          e.preventDefault();
          latest.current.onSelect(current);
          break;
        case ' ':
        case 'x':
        case 'X':
          if (!current || !latest.current.canEditTask(current)) return;
          e.preventDefault();
          latest.current.onToggle(current);
          break;
        default:
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [keyboardEnabled]);

  return (
    <section className="flex min-w-0 flex-1 flex-col overflow-hidden bg-app-surface">
      <div className="flex flex-col gap-3 px-4 pb-3 pt-4 md:px-6 md:pt-5">
        <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3 max-md:flex-nowrap max-md:items-center">
          <div className="flex min-w-0 flex-col gap-0.5">
            <h1 className="text-lg font-semibold leading-tight text-app-text">{title}</h1>
            <span className="text-sm text-app-text-subtle">{subtitle}</span>
          </div>
          <div className="hidden flex-wrap items-center gap-2 md:flex">
            {viewSelect}
            {/* Darstellung (wie) steht rechts, getrennt von der Suche (was) */}
            {showGroupBy && (
              <>
                <span className="text-xs text-app-text-subtle">Gruppieren</span>
                <SegmentedControl options={GROUP_OPTIONS} value={groupBy} onChange={onGroupBy} ariaLabel="Gruppieren nach" />
              </>
            )}
          </div>
          {/* Mobil: nur Icons — Suche, Gruppieren (natives Auswahlmenü), Neu */}
          <div className="flex shrink-0 items-center gap-1 md:hidden">
            <button
              type="button"
              aria-label="To-dos durchsuchen"
              aria-pressed={showSearch}
              // Fokus im Suchfeld lassen, sonst schließt dessen Blur die Suche vor dem Klick.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (showSearch && !query) {
                  setSearchOpen(false);
                  return;
                }
                // Erst sichtbar machen, dann in derselben Geste fokussieren — sonst öffnet iOS die Tastatur nicht.
                flushSync(() => setSearchOpen(true));
                searchRef.current?.focus();
              }}
              className={`${MOBILE_ICON_BTN_CLASS} ${showSearch ? 'bg-app-bg text-app-text' : ''}`}
            >
              <Search className="h-[18px] w-[18px]" strokeWidth={1.8} />
            </button>
            {showGroupBy && <GroupByMenu value={groupBy} onChange={onGroupBy} />}
            {onCreate && (
              <span className="ml-1">
                <MobileCreateButton onClick={onCreate} />
              </span>
            )}
          </div>
        </div>
        {viewChips}
      </div>

      {/* Mobil nur die Suche, und nur wenn geöffnet */}
      <div className={`${showSearch ? 'flex' : 'hidden'} flex-col gap-2 px-4 pb-3 sm:flex-row md:flex md:px-6`}>
        <label
          className={`relative h-10 shrink-0 items-center sm:w-1/3 sm:min-w-[200px] sm:max-w-sm ${showSearch ? 'flex' : 'hidden'} md:flex`}
        >
          <Search className="pointer-events-none absolute left-3 h-4 w-4 text-app-text-subtle" />
          <input
            ref={searchRef}
            type="search"
            data-todo-list-input
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            onBlur={() => setSearchOpen(false)}
            placeholder="To-dos durchsuchen …"
            aria-label="To-dos durchsuchen"
            className="h-10 w-full rounded-md border border-app-border bg-app-surface pl-9 pr-3 text-sm text-app-text outline-none transition-colors placeholder:text-app-text-subtle focus:border-pipedrive-blue focus:ring-2 focus:ring-pipedrive-blue/20"
          />
        </label>
        {canEdit && showQuickAdd && (
          <div className="hidden h-10 min-w-0 flex-1 items-center gap-2.5 rounded-md border border-app-border-strong bg-app-surface md:flex px-3 transition-colors focus-within:border-pipedrive-blue focus-within:ring-2 focus-within:ring-pipedrive-blue/20">
            <Plus className="h-4 w-4 shrink-0 text-pipedrive-blue" strokeWidth={2} />
            <input
              ref={quickInputRef}
              data-todo-list-input
              value={quick}
              onChange={(e) => setQuick(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
                const t = quick.trim();
                if (!t) return;
                e.preventDefault();
                onQuickAdd(t);
                setQuick('');
              }}
              placeholder="To-do hinzufügen …"
              aria-label="To-do hinzufügen"
              className="min-w-0 flex-1 bg-transparent text-sm text-app-text outline-none placeholder:text-app-text-subtle"
            />
            <span className="hidden whitespace-nowrap text-xs text-app-text-subtle lg:inline">Enter zum Anlegen</span>
          </div>
        )}
      </div>

      <div className="app-scrollbar min-h-0 flex-1 overflow-y-auto px-4 pb-8 md:px-6">
        {loading ? (
          <InlineLoadingState label="To-dos werden geladen…" className="py-16" />
        ) : failed && groups.length === 0 ? null : groups.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-4 py-16 text-center">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-green-50 text-green-600">
              <Check className="h-5 w-5" strokeWidth={2} />
            </div>
            <div className="text-[15px] font-semibold text-app-text">{query.trim() ? 'Keine Treffer' : emptyTitle}</div>
            <div className="text-sm text-app-text-subtle">
              {query.trim() ? 'Kein To-do passt zur Suche.' : emptyText}
            </div>
          </div>
        ) : (
          groups.map((group) => (
            <section key={group.key} className="mb-4 flex flex-col">
              <div className="flex items-center gap-2 border-b border-app-border py-2">
                <span
                  className={`${TASK_SECTION_LABEL_CLASS} !font-semibold ${group.tone === 'default' ? '' : TONE_TEXT_CLASS[group.tone]}`}
                >
                  {group.label}
                </span>
                <span className="text-xs tabular-nums text-app-text-subtle">{group.tasks.length}</span>
              </div>
              {group.tasks.map((task) => (
                <TaskRow
                  key={task.id}
                  task={task}
                  assignee={task.assigned_user_id ? membersById.get(task.assigned_user_id) ?? null : null}
                  today={today}
                  selected={task.id === selectedId}
                  focused={task.id === focusedId}
                  canEdit={canEditTask(task)}
                  onSelect={() => {
                    setFocusedId(task.id);
                    onSelect(task);
                  }}
                  onToggle={() => onToggle(task)}
                />
              ))}
            </section>
          ))
        )}
        {footer}
      </div>
    </section>
  );
}
